/**
 * AbilityBorrowing — pure helpers for the `with*From()` family in
 * AbilityBuilder. Takes an ability ID, clones its subconfig, applies
 * overrides, returns a new config (or null if the source lacks it).
 *
 * Extracted from AbilityBuilder.ts so the builder can stay focused on
 * fluent orchestration. These helpers are pure functions — no `this`
 * state, no builder coupling — so unit-testable without instantiating
 * a whole AbilityBuilder.
 *
 * Circular-dep safety: imports from AbilityBuilder are `import type`
 * only. Runtime imports from AbilityRegistry + VFXEffectRegistry.
 */

import { abilityRegistry, BehaviorConfig } from './AbilityRegistry';
import { VFXEffectRegistry, VFXPrimitive } from '../../../effects/VFXEffectRegistry';
import { gameLogger } from '../../../../utils/GameLogger';
import type { ProjectileConfig, VFXConfig } from './AbilityBuilder';

/**
 * Clone the projectile config of `sourceAbilityId` with optional overrides.
 * Returns null + logs a warning if the source has no projectile config.
 */
export function borrowProjectile(
  sourceAbilityId: string,
  overrides?: Partial<ProjectileConfig>,
): ProjectileConfig | null {
  const source = abilityRegistry.getMetadata(sourceAbilityId);
  if (!source?.projectile) {
    gameLogger.warn('abilities', `borrowProjectile: ${sourceAbilityId} has no projectile config`);
    return null;
  }
  const cloned = structuredClone(source.projectile);
  return {
    damage: cloned.damage as number,
    speed: cloned.speed,
    shape: cloned.shape,
    color: cloned.color,
    width: cloned.width,
    height: cloned.height,
    gravity: cloned.gravity,
    rotationSpeed: cloned.rotationSpeed,
    lifespan: cloned.lifespan,
    strokeColor: cloned.strokeColor,
    strokeWidth: cloned.strokeWidth,
    ...overrides,
  };
}

/**
 * Clone behavior configs from `sourceAbilityId`. Optionally filter by type.
 * Returns null + logs a warning if the source has no behaviors.
 */
export function borrowBehaviors(
  sourceAbilityId: string,
  filterTypes?: BehaviorConfig['type'][],
): BehaviorConfig[] | null {
  const source = abilityRegistry.getMetadata(sourceAbilityId);
  if (!source?.behaviors) {
    gameLogger.warn('abilities', `borrowBehaviors: ${sourceAbilityId} has no behaviors`);
    return null;
  }
  const cloned = structuredClone(source.behaviors);
  return filterTypes ? cloned.filter(b => filterTypes.includes(b.type)) : cloned;
}

/**
 * Clone VFX config from `sourceAbilityId` with optional overrides.
 * Returns null + logs a warning if the source has no VFX config.
 */
export function borrowVFX(
  sourceAbilityId: string,
  overrides?: Partial<VFXConfig>,
): VFXConfig | null {
  const source = abilityRegistry.getMetadata(sourceAbilityId);
  if (!source?.vfx) {
    gameLogger.warn('abilities', `borrowVFX: ${sourceAbilityId} has no VFX config`);
    return null;
  }
  const cloned = structuredClone(source.vfx);
  return {
    effectId: cloned.muzzleFlash || sourceAbilityId,
    color: overrides?.color ?? cloned.color,
    scale: overrides?.scale ?? cloned.scale,
    duration: overrides?.duration ?? cloned.duration,
    ...overrides,
  };
}

/**
 * Cherry-pick specific primitive layers from a registered VFX effect.
 * If `layerIndices` is omitted, returns all primitives.
 * Returns null + logs a warning if the effect is not registered.
 */
export function borrowVFXLayers(
  effectId: string,
  layerIndices?: number[],
): VFXPrimitive[] | null {
  const config = VFXEffectRegistry.get(effectId);
  if (!config) {
    gameLogger.warn('abilities', `borrowVFXLayers: effect ${effectId} not found`);
    return null;
  }
  const layers = layerIndices
    ? config.primitives.filter((_, i) => layerIndices.includes(i))
    : config.primitives;
  return structuredClone(layers);
}
